import { useState, useEffect } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import { DataItem } from './UploadSection';

interface DataListProps {
  refreshTrigger: number;
}

export default function DataList({ refreshTrigger }: DataListProps) {
  const { getClients } = useAuth();
  const clients = getClients();
  
  const [dataItems, setDataItems] = useState<DataItem[]>([]);
  const [filteredItems, setFilteredItems] = useState<DataItem[]>([]);
  const [selectedClient, setSelectedClient] = useState<string>('all');
  const [selectedProject, setSelectedProject] = useState<string>('all');
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    loadData();
  }, [refreshTrigger]);

  useEffect(() => {
    applyFilters();
  }, [dataItems, selectedClient, selectedProject, searchQuery]);

  const loadData = () => {
    const storedData = localStorage.getItem('admin_data_items');
    if (storedData) {
      try {
        const items: DataItem[] = JSON.parse(storedData);
        setDataItems(items);
      } catch (e) {
        console.error('Failed to parse data items', e);
        setDataItems([]);
      }
    } else {
      setDataItems([]);
    }
  };

  const applyFilters = () => {
    let filtered = [...dataItems];

    // Filter by client
    if (selectedClient !== 'all') {
      filtered = filtered.filter(item => item.clientId === selectedClient);
    }

    // Filter by project
    if (selectedProject !== 'all') {
      filtered = filtered.filter(item => item.projectNumber === selectedProject);
    }

    // Filter by search query
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(item =>
        item.title.toLowerCase().includes(query) ||
        item.description.toLowerCase().includes(query) ||
        item.projectNumber?.toLowerCase().includes(query) ||
        item.projectName?.toLowerCase().includes(query)
      );
    }

    // Sort by upload date (newest first)
    filtered.sort((a, b) => new Date(b.uploadedAt).getTime() - new Date(a.uploadedAt).getTime());

    setFilteredItems(filtered);
  };

  const handleDelete = (id: string) => {
    const updatedItems = dataItems.filter(item => item.id !== id);
    localStorage.setItem('admin_data_items', JSON.stringify(updatedItems));
    setDataItems(updatedItems);
    setDeleteConfirm(null);
  };

  const handleDownload = (item: DataItem) => {
    if (item.type === 'file' && item.fileUrl) {
      const link = document.createElement('a');
      link.href = item.fileUrl;
      link.download = item.fileName || 'download';
      link.click();
    }
  };

  const handleView = (item: DataItem) => {
    if (item.type === 'file' && item.fileUrl) {
      window.open(item.fileUrl, '_blank');
    }
  };

  const getClientName = (clientId?: string): string => {
    if (!clientId) return 'Unknown Client';
    const client = clients.find(c => c.id === clientId);
    return client ? client.name : 'Unknown Client';
  };

  const getFileIcon = (fileType?: string): string => {
    if (!fileType) return 'ri-file-line';
    if (fileType.includes('pdf')) return 'ri-file-pdf-line';
    if (fileType.includes('word') || fileType.includes('document')) return 'ri-file-word-line';
    if (fileType.includes('excel') || fileType.includes('spreadsheet')) return 'ri-file-excel-line';
    if (fileType.includes('image')) return 'ri-image-line';
    return 'ri-file-line';
  };

  // Get unique projects for the selected client
  const getAvailableProjects = (): string[] => {
    let items = dataItems;
    if (selectedClient !== 'all') {
      items = items.filter(item => item.clientId === selectedClient);
    }
    const projects = items
      .filter(item => item.projectNumber)
      .map(item => item.projectNumber!);
    return Array.from(new Set(projects));
  };

  // Group items by project
  const groupedByProject = filteredItems.reduce((acc, item) => {
    const projectKey = item.projectNumber || 'unassigned';
    if (!acc[projectKey]) {
      acc[projectKey] = {
        projectNumber: item.projectNumber || 'Unassigned',
        projectName: item.projectName || 'No Project',
        items: []
      };
    }
    acc[projectKey].items.push(item);
    return acc;
  }, {} as Record<string, { projectNumber: string; projectName: string; items: DataItem[] }>);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-gray-900">Uploaded Data</h2>
          <p className="text-sm text-gray-600 mt-1">
            {filteredItems.length} {filteredItems.length === 1 ? 'item' : 'items'} 
            {selectedClient !== 'all' || selectedProject !== 'all' || searchQuery ? ' (filtered)' : ''}
          </p>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white border border-gray-200 rounded-lg p-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Client Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Filter by Client
            </label>
            <select
              value={selectedClient}
              onChange={(e) => {
                setSelectedClient(e.target.value);
                setSelectedProject('all'); // Reset project filter
              }}
              className="w-full px-4 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent outline-none cursor-pointer"
            >
              <option value="all">All Clients</option>
              {clients.map((client) => (
                <option key={client.id} value={client.id}>
                  {client.name}
                </option>
              ))}
            </select>
          </div>

          {/* Project Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Filter by Project
            </label>
            <select
              value={selectedProject}
              onChange={(e) => setSelectedProject(e.target.value)}
              className="w-full px-4 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent outline-none cursor-pointer"
            >
              <option value="all">All Projects</option>
              {getAvailableProjects().map((project) => (
                <option key={project} value={project}>
                  {project}
                </option>
              ))}
            </select>
          </div>

          {/* Search */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Search
            </label>
            <div className="relative">
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search by title, description..."
                className="w-full px-4 py-2 pl-10 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent outline-none"
              />
              <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"></i>
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 cursor-pointer"
                >
                  <i className="ri-close-line"></i>
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Clear Filters */}
        {(selectedClient !== 'all' || selectedProject !== 'all' || searchQuery) && (
          <div className="mt-4 pt-4 border-t border-gray-200">
            <button
              onClick={() => {
                setSelectedClient('all');
                setSelectedProject('all');
                setSearchQuery('');
              }}
              className="text-sm text-gray-600 hover:text-gray-900 font-medium cursor-pointer whitespace-nowrap"
            >
              <i className="ri-filter-off-line mr-1"></i>
              Clear All Filters
            </button>
          </div>
        )}
      </div>

      {/* Data List - Grouped by Project */}
      {filteredItems.length === 0 ? (
        <div className="bg-white border border-gray-200 rounded-lg p-12 text-center">
          <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <i className="ri-folder-open-line text-3xl text-gray-400"></i>
          </div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">
            {dataItems.length === 0 ? 'No data uploaded yet' : 'No matching items'}
          </h3>
          <p className="text-sm text-gray-600">
            {dataItems.length === 0 
              ? 'Upload your first document to get started'
              : 'Try adjusting your filters or search query'
            }
          </p>
        </div>
      ) : (
        <div className="space-y-6">
          {Object.entries(groupedByProject).map(([projectKey, projectData]) => (
            <div key={projectKey} className="bg-white border border-gray-200 rounded-lg overflow-hidden">
              {/* Project Header */}
              <div className="bg-gray-50 border-b border-gray-200 px-6 py-4">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">
                      {projectData.projectNumber}
                    </h3>
                    <p className="text-sm text-gray-600 mt-1">{projectData.projectName}</p>
                  </div>
                  <span className="px-3 py-1 bg-gray-200 text-gray-700 text-xs font-medium rounded-full">
                    {projectData.items.length} {projectData.items.length === 1 ? 'item' : 'items'}
                  </span>
                </div>
              </div>

              {/* Project Items */}
              <div className="divide-y divide-gray-200">
                {projectData.items.map((item) => (
                  <div key={item.id} className="p-6 hover:bg-gray-50 transition-colors">
                    <div className="flex items-start gap-4">
                      {/* Icon */}
                      <div className="w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center flex-shrink-0">
                        <i className={`${item.type === 'text' ? 'ri-file-text-line' : getFileIcon(item.fileType)} text-2xl text-gray-600`}></i>
                      </div>

                      {/* Content */}
                      <div className="flex-1 min-w-0">
                        <h4 className="text-base font-semibold text-gray-900 mb-1">{item.title}</h4>
                        {item.description && (
                          <p className="text-sm text-gray-600 mb-2">{item.description}</p>
                        )}
                        <div className="flex flex-wrap items-center gap-4 text-xs text-gray-500">
                          <span>
                            <i className="ri-user-line mr-1"></i>
                            {getClientName(item.clientId)}
                          </span>
                          {item.fileName && (
                            <span>
                              <i className="ri-file-line mr-1"></i>
                              {item.fileName}
                            </span>
                          )}
                          <span>
                            <i className="ri-calendar-line mr-1"></i>
                            {new Date(item.uploadedAt).toLocaleDateString('en-US', {
                              year: 'numeric',
                              month: 'short',
                              day: 'numeric'
                            })}
                          </span>
                        </div>
                      </div>

                      {/* Actions */}
                      <div className="flex items-center gap-2 flex-shrink-0">
                        {item.type === 'file' && (
                          <>
                            <button
                              onClick={() => handleView(item)}
                              className="w-9 h-9 flex items-center justify-center text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors cursor-pointer"
                              title="View file"
                            >
                              <i className="ri-eye-line text-base"></i>
                            </button>
                            <button
                              onClick={() => handleDownload(item)}
                              className="w-9 h-9 flex items-center justify-center text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors cursor-pointer"
                              title="Download file"
                            >
                              <i className="ri-download-line text-base"></i>
                            </button>
                          </>
                        )}
                        {deleteConfirm === item.id ? (
                          <div className="flex items-center gap-1">
                            <button
                              onClick={() => handleDelete(item.id)}
                              className="px-3 py-1 bg-red-600 text-white text-xs font-medium rounded hover:bg-red-700 transition-colors whitespace-nowrap cursor-pointer"
                            >
                              Confirm
                            </button>
                            <button
                              onClick={() => setDeleteConfirm(null)}
                              className="px-3 py-1 bg-gray-200 text-gray-700 text-xs font-medium rounded hover:bg-gray-300 transition-colors whitespace-nowrap cursor-pointer"
                            >
                              Cancel
                            </button>
                          </div>
                        ) : (
                          <button
                            onClick={() => setDeleteConfirm(item.id)}
                            className="w-9 h-9 flex items-center justify-center text-red-600 hover:text-red-700 hover:bg-red-50 rounded-lg transition-colors cursor-pointer"
                            title="Delete"
                          >
                            <i className="ri-delete-bin-line text-base"></i>
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}